/**
 * Frozen enum member registries for compact codec (Task 5).
 * Each enum is defined as a readonly array and frozen at runtime.
 * The generic codec converts string members to numeric indices for tuple storage.
 *
 * Spec: docs/superpowers/specs/YYYY-MM-DD-server-stage-validation-v2-design.md §6
 */

// ─────────────────────────────────────────────────────────────────────────────
// Core game modes
// ─────────────────────────────────────────────────────────────────────────────

export const GAME_MODE = ['STAGE', 'LEAGUE', 'VALHALLA', 'DEGEN_LEAGUE'] as const;
Object.freeze(GAME_MODE);

export const SEASON_TYPE = ['REGULAR_SEASON', 'CHAMPIONSHIP'] as const;
Object.freeze(SEASON_TYPE);

export const SUMMON_TYPE = ['STANDARD', 'PREVIEW'] as const;
Object.freeze(SUMMON_TYPE);

// ─────────────────────────────────────────────────────────────────────────────
// Degen league specifics
// ─────────────────────────────────────────────────────────────────────────────

export const DEGEN_DIFFICULTY = ['norns', 'jotnar', 'ragnarok'] as const;
Object.freeze(DEGEN_DIFFICULTY);

export const BLESSING_STAT = [
  'HP',
  'ATK',
  'FIRE_RATE',
  'SHOTS',
  'PROJECTILE_SPEED',
  'PIERCING',
  'MOVEMENT_SPEED',
  'CRIT_CHANCE',
  'KNOCKBACK',
  'CRIT_DAMAGE',
  'DODGE_CHANCE',
  'MANA',
  'RELOAD_SPEED',
  'INVINCIBILITY_DURATION',
  'HIT_KNOCKBACK',
  'RANGE',
  'SOUL_COLLECTION_RANGE',
] as const;
Object.freeze(BLESSING_STAT);

export const BLESSING_DIRECTION = ['BUFF', 'DEBUFF'] as const;
Object.freeze(BLESSING_DIRECTION);

export const BLESSING_TARGET = ['PLAYER', 'ENEMY'] as const;
Object.freeze(BLESSING_TARGET);

export const CONDITIONAL_DIFFICULTY = ['low', 'mid', 'high', 'extreme'] as const;
Object.freeze(CONDITIONAL_DIFFICULTY);

/**
 * 디젠 시즌 상금풀 통화. 슬롯 0 = 'ncg' 라 재시딩된 레거시 행이 기존 NCG 동작으로
 * 디코딩된다(아직 재시딩 전인 짧은 튜플은 이 필드가 undefined 로 나오므로 소비
 * 경계에서 접는다). 순서 변경 금지 — 튜플 인덱스가 와이어 계약이다.
 * 타입 미러: `master-data/types/degen-season.types.ts` 의 `DegenPrizeCurrency`.
 */
export const DEGEN_PRIZE_CURRENCY = ['ncg', 'glory'] as const;
Object.freeze(DEGEN_PRIZE_CURRENCY);

export const TRACKER_TYPE = [
  'HP_RATIO_CLEAR',
  'TIME_RATIO_CLEAR',
  'OVERKILL_RATIO',
  'WAVESHOP_SKIP',
  'SKILL_UNUSED',
  'KILLSTREAK',
  'SOUL_COLLECTION',
] as const;
Object.freeze(TRACKER_TYPE);

// ─────────────────────────────────────────────────────────────────────────────
// Quest types
// ─────────────────────────────────────────────────────────────────────────────

export const QUEST_TYPE = ['DAILY', 'WEEKLY', 'CUMULATIVE'] as const;
Object.freeze(QUEST_TYPE);

// ─────────────────────────────────────────────────────────────────────────────
// Registry
// ─────────────────────────────────────────────────────────────────────────────

export const ENUM_REGISTRY = {
  GameMode: GAME_MODE,
  SeasonType: SEASON_TYPE,
  SummonType: SUMMON_TYPE,
  DegenDifficulty: DEGEN_DIFFICULTY,
  BlessingStat: BLESSING_STAT,
  BlessingDirection: BLESSING_DIRECTION,
  BlessingTarget: BLESSING_TARGET,
  ConditionalDifficulty: CONDITIONAL_DIFFICULTY,
  DegenPrizeCurrency: DEGEN_PRIZE_CURRENCY,
  TrackerType: TRACKER_TYPE,
  QuestType: QUEST_TYPE,
} as const;
Object.freeze(ENUM_REGISTRY);

export type EnumName = keyof typeof ENUM_REGISTRY;

// ─────────────────────────────────────────────────────────────────────────────
// Index helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Convert an enum member string to its numeric index in the frozen array.
 * Throws if the member is not found.
 */
export function enumToIndex(members: readonly string[], value: string): number {
  const idx = members.indexOf(value);
  if (idx < 0) {
    throw new Error(`unknown enum member: ${value} (allowed: ${members.join('|')})`);
  }
  return idx;
}

/**
 * Convert a numeric index back to the enum member string.
 * Throws if the index is out of range.
 */
export function enumFromIndex<T extends string>(members: readonly T[], index: number): T {
  if (index < 0 || index >= members.length) {
    throw new Error(`enum index out of range: ${index} (size ${members.length})`);
  }
  return members[index];
}
